import {
  LabelEntityTypes,
  LabelFormattingInfo,
  ComponentLabelSource,
  ReferenceLabelSource,
  COMPONENT_OR_REFERENCE_DESCRIPTION_FIELD_LABEL,
} from '@ardoq/data-model';
import { validate as validateUuid } from 'uuid';
import { SelectOption, SelectOptionsOrGroups } from '@ardoq/select';
import { APIFieldAttributes, APIFieldType, ViewIds } from '@ardoq/api-types';
import { isNil, uniq, uniqBy } from 'lodash';
import { isTruthy } from '@ardoq/common-helpers';
import { getFieldOptionsWithDefault } from '@ardoq/perspectives-sidebar';
import { dateRangeOperations } from '@ardoq/date-range';
import { viewIdToUserFriendlyName } from 'views/metaInfoConsts';
import { getRestrictedMultilabelFormattingWarningMessage } from 'getRestrictedMultilabelFormattingWarningMessage';
import { isViewWithoutFullyMultiLabelsSupport } from 'views/environmentals/getViewpointModeSupportedViews';
import { hasReferenceBubbleChartFeature } from 'tabview/bubbleChart/referenceBubbleChart/hasReferenceBubbleChartFeature';
import { NotificationData } from '@ardoq/perspectives';
import { PermissionContext } from '@ardoq/access-control';
import {
  fieldIsConfidential,
  toConfidentialFieldSelectOption,
} from 'utils/confidentialFieldUtil';
import { type I18nLocale, t } from '@ardoq/i18n';
import { toFieldTypeKey } from 'streams/fields/fields$';

const DIVIDER = '~~\\~~';
const composeLabelRuleKey = (labelFormatting: LabelFormattingInfo) => {
  const { type, typeName, showFieldName } = labelFormatting;

  return [type, typeName, showFieldName].join(DIVIDER);
};
const decomposeLabelRuleKey = (key: string) => {
  const [type, typeName, showFieldName] = key.split(DIVIDER);

  return {
    type,
    typeName,
    showFieldName: showFieldName === 'true',
  };
};

const defaultFieldNamesSet = new Set<string>([
  ComponentLabelSource.TYPE,
  ReferenceLabelSource.DISPLAY_TEXT,
  COMPONENT_OR_REFERENCE_DESCRIPTION_FIELD_LABEL,
]);

const getAllTypesLabel = (type: LabelEntityTypes, i18nLocale: I18nLocale) =>
  type === 'component'
    ? t(
        i18nLocale,
        'perspectiveSidebar:labelsTab.allComponentTypes',
        'All component types'
      )
    : t(
        i18nLocale,
        'perspectiveSidebar:labelsTab.allReferenceTypes',
        'All reference types'
      );

const buildNotifications = (i18nLocale: I18nLocale) => ({
  fieldUsedInAllTypes: (type: string) =>
    t(
      i18nLocale,
      'perspectiveSidebar:labelsTab.fieldUsedInAllTypes',
      { type },
      'This field is already on a label applied to all {{type}} types'
    ),
  fieldUsedInSpecificTypes: (types: string, entityType: string) =>
    types.includes(',')
      ? t(
          i18nLocale,
          'perspectiveSidebar:labelsTab.fieldUsedInSpecificTypes_plural',
          { types, entityType },
          'This field is already on a label applied to the {{types}} {{entityType}} types.'
        )
      : t(
          i18nLocale,
          'perspectiveSidebar:labelsTab.fieldUsedInSpecificTypes',
          { types, entityType },
          'This field is already on a label applied to the {{types}} {{entityType}} type.'
        ),
  typeUsedInDifferentLabel: (entityType: string) =>
    t(
      i18nLocale,
      'perspectiveSidebar:labelsTab.typeUsedInDifferentLabel',
      { entityType },
      'This {{entityType}} type is already used in a different label'
    ),
});

const addDisabledStateToOption = (
  option: SelectOption<string>,
  message: string
): SelectOption<string> => ({
  ...option,
  isDisabled: true,
  popoverContent: message,
});

const toTypeOptions = (
  names: string[],
  allTypesLabel: string,
  alreadyUsedTypes: Set<string>,
  entityType: LabelEntityTypes,
  notifications: ReturnType<typeof buildNotifications>
): SelectOption<string | null>[] =>
  names.map(name => ({
    label: name,
    value: name === allTypesLabel ? null : name,
    ...(alreadyUsedTypes.has(name) && {
      isDisabled: true,
      popoverContent: notifications.typeUsedInDifferentLabel(entityType),
    }),
  }));

type GetFieldOptionLabelArgs = Pick<
  APIFieldAttributes,
  'label' | 'name' | 'type'
>;
export const getFieldOptionLabel = (fieldData: GetFieldOptionLabelArgs) =>
  dateRangeOperations.isPartOfDateRangeField(fieldData)
    ? dateRangeOperations.dashFormatDateRangeFieldLabel(fieldData.label)
    : fieldData.label;

const toFieldOptions = (fields: APIFieldAttributes[]) =>
  fields.map(field => ({
    label: getFieldOptionLabel(field),
    value: field.name,
  }));

const getTypeOptions = (
  labelFormatting: LabelFormattingInfo,
  validTypeOptions: Map<string, SelectOptionsOrGroups<string>>,
  alreadyUsedTypes: Set<string>,
  entityType: LabelEntityTypes,
  allTypesLabel: string,
  notifications: ReturnType<typeof buildNotifications>
) => {
  const typeName = labelFormatting.typeName || allTypesLabel;
  const isUnset = validateUuid(typeName);
  const isSelectedTypeValid = isUnset || validTypeOptions.has(typeName);

  // Move AllTypes to the beginning
  const typeNames = [
    allTypesLabel,
    ...Array.from(validTypeOptions.keys()).filter(
      name => name !== allTypesLabel
    ),
  ];

  const typeOptions = isUnset
    ? toTypeOptions(
        typeNames,
        allTypesLabel,
        alreadyUsedTypes,
        entityType,
        notifications
      )
    : toTypeOptions(
        uniq([typeName, ...typeNames]),
        allTypesLabel,
        alreadyUsedTypes,
        entityType,
        notifications
      );

  return {
    typeOptions: typeOptions,
    isSelectedTypeValid,
  };
};

const enhanceOptionsByTypeWithAllTypes = (
  optionsByType: Map<string, SelectOption<string>[]>,
  allTypesLabel: string
): Map<string, SelectOption<string>[]> => {
  const allPossibleOptions = uniqBy(
    Array.from(optionsByType.values()).flat(),
    'value'
  );
  const enhancedOptionsByType = new Map(optionsByType);
  enhancedOptionsByType.set(allTypesLabel, allPossibleOptions);
  return enhancedOptionsByType;
};

const createFieldOptionByName = (
  fieldName: string,
  fieldLabelByName: ReadonlyMap<string, string>
): SelectOption<string> => ({
  label: fieldLabelByName.get(fieldName) ?? fieldName,
  value: fieldName,
});

const createBaseFieldOptions = (
  labelFormatting: LabelFormattingInfo,
  isSelectedTypeValid: boolean,
  validTypeOptions: Map<string, SelectOption<string>[]>,
  allTypesLabel: string,
  fieldLabelByName: ReadonlyMap<string, string>
): SelectOption<string>[] => {
  if (isSelectedTypeValid) {
    const baseOptions =
      validTypeOptions.get(labelFormatting.typeName || allTypesLabel) ?? [];

    const availableFieldValues = new Set(baseOptions.map(o => o.value));
    const missingFields = labelFormatting.fields.filter(
      fieldName =>
        !availableFieldValues.has(fieldName) &&
        !defaultFieldNamesSet.has(fieldName)
    );

    if (missingFields.length > 0) {
      const fallbackOptions = missingFields.map(fieldName =>
        createFieldOptionByName(fieldName, fieldLabelByName)
      );
      return [...fallbackOptions, ...baseOptions];
    }

    return baseOptions;
  }

  return labelFormatting.fields
    .map(fieldName => createFieldOptionByName(fieldName, fieldLabelByName))
    .filter(({ value }) => !defaultFieldNamesSet.has(value));
};

const getFieldsUsedInRules = (labelFormattingArray: LabelFormattingInfo[]) => {
  const fieldsUsedByType = new Map<string, Set<string>>();

  labelFormattingArray.forEach(labelFormatting => {
    const typeKey = composeLabelRuleKey(labelFormatting);

    if (!fieldsUsedByType.has(typeKey)) {
      fieldsUsedByType.set(typeKey, new Set<string>());
    }

    const fieldSet = fieldsUsedByType.get(typeKey)!;
    labelFormatting.fields.forEach(field => {
      fieldSet.add(field);
    });
  });

  return fieldsUsedByType;
};

/**
 * Applies disabled state to an individual option based on field usage in existing rules
 * @returns Original or disabled option with popover message
 */
const maybeDisableOption = (
  option: SelectOption<string>,
  labelFormatting: LabelFormattingInfo,
  fieldsUsedInRules: Map<string, Set<string>>,
  notifications: ReturnType<typeof buildNotifications>
): SelectOption<string> => {
  const currentRuleKey = composeLabelRuleKey(labelFormatting);
  const allTypeKey = composeLabelRuleKey({
    ...labelFormatting,
    showFieldName: true,
    typeName: null,
  });

  // Check if field is already used in a rule that applies to all types
  // But don't disable if it's the current rule we're checking
  const isFieldUsedInAllTypes =
    fieldsUsedInRules.get(allTypeKey)?.has(option.value) &&
    allTypeKey !== currentRuleKey;

  if (isFieldUsedInAllTypes) {
    return addDisabledStateToOption(
      option,
      notifications.fieldUsedInAllTypes(labelFormatting.type)
    );
  }

  const specificTypesUsingField: string[] = [];

  // Check if field is used in specific type rules
  if (labelFormatting.typeName) {
    // Check if field is used in a rule for the same type but with different showFieldName setting
    const fieldsOfSameTypeWithDifferentShowFieldName = fieldsUsedInRules.get(
      composeLabelRuleKey({
        ...labelFormatting,
        showFieldName: !labelFormatting.showFieldName,
      })
    );

    if (fieldsOfSameTypeWithDifferentShowFieldName?.has(option.value)) {
      specificTypesUsingField.push(labelFormatting.typeName);
    }
  } else {
    // When no specific type is selected, check all types for usage of this field
    fieldsUsedInRules.forEach((fields, key) => {
      const { type, typeName } = decomposeLabelRuleKey(key);

      if (
        type === labelFormatting.type &&
        typeName &&
        fields.has(option.value)
      ) {
        specificTypesUsingField.push(typeName);
      }
    });
  }

  if (specificTypesUsingField.length) {
    return addDisabledStateToOption(
      option,
      notifications.fieldUsedInSpecificTypes(
        specificTypesUsingField.join(', '),
        labelFormatting.type
      )
    );
  }

  return option;
};

const maybeDisableOptions = (
  options: SelectOptionsOrGroups<string>,
  labelFormatting: LabelFormattingInfo,
  fieldsUsedInRules: Map<string, Set<string>>,
  notifications: ReturnType<typeof buildNotifications>
): SelectOptionsOrGroups<string> =>
  options.map(item => {
    if ('options' in item) {
      return {
        ...item,
        options: item.options.map(option =>
          maybeDisableOption(
            option,
            labelFormatting,
            fieldsUsedInRules,
            notifications
          )
        ),
      };
    }
    return maybeDisableOption(
      item,
      labelFormatting,
      fieldsUsedInRules,
      notifications
    );
  });

const maybeAddConfidentialShield = (
  option: SelectOption<string>,
  permissionContext: PermissionContext,
  fieldTypesByName: ReadonlyMap<string, APIFieldType>,
  i18nLocale: I18nLocale
): SelectOption<string> => {
  if (
    !fieldIsConfidential(permissionContext, {
      name: option.value,
      type: fieldTypesByName.get(option.value),
    })
  ) {
    return option;
  }
  return toConfidentialFieldSelectOption(
    option,
    t(
      i18nLocale,
      'perspectiveSidebar:labelsTab.confidentialFieldHidden',
      'Confidential field may be hidden for some users'
    ),
    'description'
  );
};

const withConfidentialShield = (
  options: SelectOptionsOrGroups<string>,
  permissionContext: PermissionContext,
  fieldTypesByName: ReadonlyMap<string, APIFieldType>,
  i18nLocale: I18nLocale
): SelectOptionsOrGroups<string> =>
  options.map(item => {
    if ('options' in item) {
      return {
        ...item,
        options: item.options.map(opt =>
          maybeAddConfidentialShield(
            opt,
            permissionContext,
            fieldTypesByName,
            i18nLocale
          )
        ),
      };
    }
    return maybeAddConfidentialShield(
      item,
      permissionContext,
      fieldTypesByName,
      i18nLocale
    );
  });

export const getLabelFormattingElementProps = (
  labelFormattingArray: LabelFormattingInfo[],
  componentFieldOptionsByTypes: Map<string, SelectOption<string>[]>,
  referenceFieldOptionsByTypes: Map<string, SelectOption<string>[]>,
  isReferenceTypeDisabled: boolean,
  permissionContext: PermissionContext,
  fieldTypesByName: ReadonlyMap<string, APIFieldType>,
  fieldLabelByName: ReadonlyMap<string, string>,
  i18nLocale: I18nLocale
) => {
  const allUsedComponentTypes = new Set<string>(
    labelFormattingArray
      .map(({ type, typeName }) => type === 'component' && typeName)
      .filter(isTruthy)
  );
  const allUsedReferenceTypes = new Set<string>(
    labelFormattingArray
      .map(({ type, typeName }) => type === 'reference' && typeName)
      .filter(isTruthy)
  );

  const fieldsUsedInRules = getFieldsUsedInRules(labelFormattingArray);
  const notifications = buildNotifications(i18nLocale);

  return labelFormattingArray.map((labelFormatting, index) => {
    const { type, typeName } = labelFormatting;
    const isComponentFormatting = type === 'component';
    const optionsByType = isComponentFormatting
      ? componentFieldOptionsByTypes
      : referenceFieldOptionsByTypes;
    const allTypesLabel = getAllTypesLabel(type, i18nLocale);
    const validTypeOptions = enhanceOptionsByTypeWithAllTypes(
      optionsByType,
      allTypesLabel
    );

    const alreadyUsedTypesCopy = new Set(
      isComponentFormatting ? allUsedComponentTypes : allUsedReferenceTypes
    );
    if (typeName) {
      alreadyUsedTypesCopy.delete(typeName);
    }

    const { isSelectedTypeValid, typeOptions } = getTypeOptions(
      labelFormatting,
      validTypeOptions,
      alreadyUsedTypesCopy,
      labelFormatting.type,
      allTypesLabel,
      notifications
    );

    const baseFieldOptions = createBaseFieldOptions(
      labelFormatting,
      isSelectedTypeValid,
      validTypeOptions,
      allTypesLabel,
      fieldLabelByName
    );

    const fieldOptions = withConfidentialShield(
      maybeDisableOptions(
        getFieldOptionsWithDefault(baseFieldOptions, isComponentFormatting),
        labelFormatting,
        fieldsUsedInRules,
        notifications
      ),
      permissionContext,
      fieldTypesByName,
      i18nLocale
    );

    const isAllTypes = isNil(labelFormatting.typeName);
    const disabledCheckboxMessage = isAllTypes
      ? t(
          i18nLocale,
          'perspectiveSidebar:labelsTab.fieldNameAlwaysShown',
          'Field name is always shown on labels applied to all component or reference types'
        )
      : null;

    return {
      index,
      typeOptions: groupTypeOptions(typeOptions),
      fieldOptions,
      isComponentFormatting,
      isSelectedTypeValid,
      isDisabled: isReferenceTypeDisabled && !isComponentFormatting,
      disabledCheckboxMessage,
      labelFormatting: {
        ...labelFormatting,
        showFieldName: isAllTypes ? true : labelFormatting.showFieldName,
      },
    };
  });
};

/**
 * Map type options to groups without label to display divider in dropdown
 */
const groupTypeOptions = (
  typeOptions: SelectOption<string | null>[]
): SelectOptionsOrGroups<string | null> => {
  const allTypesOption = typeOptions.find(option => option.value === null);
  const allTypesOptionGroup = allTypesOption
    ? {
        options: [allTypesOption],
      }
    : null;
  const otherTypesOptionGroup = {
    options: typeOptions.filter(option => option.value !== null),
  };

  return [
    ...(allTypesOptionGroup ? [allTypesOptionGroup] : []),
    otherTypesOptionGroup,
  ];
};

type ComponentTypeData = {
  componentType: { id: string; name: string };
  workspaceModelId: string;
};

export const getFieldOptionsByComponentTypes = (
  componentTypes: ComponentTypeData[],
  fieldsByComponentType: ReadonlyMap<string, APIFieldAttributes[]>
): Map<string, SelectOption<string>[]> =>
  componentTypes.reduce<Map<string, SelectOption<string>[]>>(
    (
      acc,
      { componentType: { id, name: componentTypeName }, workspaceModelId }
    ) => {
      const fieldsOfType =
        fieldsByComponentType.get(toFieldTypeKey(workspaceModelId, id)) ?? [];
      const options = toFieldOptions(fieldsOfType);

      return acc.set(
        componentTypeName,
        uniqBy([...(acc.get(componentTypeName) ?? []), ...options], 'value')
      );
    },
    new Map()
  );
type ReferenceTypeData = {
  referenceType: { name: string };
  typeId: number;
  workspaceModelId: string;
};

export const getFieldOptionsByReferenceTypes = (
  referenceTypes: ReferenceTypeData[],
  fieldsByReferenceType: ReadonlyMap<string, APIFieldAttributes[]>
): Map<string, SelectOption<string>[]> =>
  referenceTypes.reduce<Map<string, SelectOption<string>[]>>(
    (acc, { typeId, referenceType, workspaceModelId }) => {
      const fieldsOfType =
        fieldsByReferenceType.get(toFieldTypeKey(workspaceModelId, typeId)) ??
        [];

      const options = toFieldOptions(fieldsOfType);

      return acc.set(
        referenceType.name,
        uniqBy([...(acc.get(referenceType.name) ?? []), ...options], 'value')
      );
    },
    new Map()
  );

const viewsWithoutReferenceFormattingSupport = [
  ViewIds.TIMELINE,
  /* The three hierarchical views draw no references at all, so a reference label rule can never
     render - the same reason Timeline is here. Not the same question as whether the
     warning fires: this is the list that stops the rule being written, and without an entry a
     user can configure one, save it with the viewpoint, and be warned about it forever. */
  ViewIds.HIERARCHICAL_TREEMAP,
  ViewIds.HIERARCHICAL_PACKING,
  ViewIds.SUNBURST,
  /* `buildSurfaceSourceData` hardcodes `referenceLabelFilters: []`, so a rule added here is
     dropped rather than applied - and the view already says as much in
     `getRestrictedMultilabelFormattingWarningMessage`. Saying it and leaving the editor enabled
     is the worse of the two. */
  ViewIds.SURFACE_3D,
  /* The other three over `buildXyzPoints`, which passes the same empty `referenceLabelFilters`. */
  ViewIds.CONTOUR,
  ViewIds.SCATTER_3D,
  ViewIds.BUBBLE_3D,
  /* `NetworkEdge` has no label field at all and nothing in `buildNetwork3DSourceData` resolves
     one, so a reference label rule is dropped here exactly as it is for the four above - and the
     view already says so in `getRestrictedMultilabelFormattingWarningMessage`. Saying it while
     leaving the editor enabled is the failure this list exists to prevent. */
  ViewIds.NETWORK_3D,
  ViewIds.SPIDER_2,
  ViewIds.POLAR,
];

export const isViewWithoutReferenceFormattingSupport = (
  viewId: ViewIds
): boolean =>
  viewId === ViewIds.BUBBLE
    ? !hasReferenceBubbleChartFeature()
    : viewsWithoutReferenceFormattingSupport.includes(viewId);

export const getDisabledReferenceFormattingMessage = (
  viewId: ViewIds,
  i18nLocale: I18nLocale
) => {
  if (isViewWithoutReferenceFormattingSupport(viewId)) {
    return t(
      i18nLocale,
      'perspectiveSidebar:labelsTab.referenceLabelsNotShown',
      { viewName: viewIdToUserFriendlyName[viewId] },
      'Reference Labels are not shown in {{viewName}}. Go to Block Diagram or Dependency Map to see and edit reference labels.'
    );
  }
  return undefined;
};

export const hasReferenceLabelFormattingRules = (
  labelFormatting: LabelFormattingInfo[]
) => labelFormatting.some(({ type }) => type === 'reference');

/**
 * A component label rule carrying at least one field.
 *
 * The fields rather than the rule, because `getAddLabelButtonProps` writes the rule on the click
 * that opens it - `{ type: 'component', fields: [] }` - so asking about the rule warns that field
 * labels are being dropped before the user has chosen one. Nothing prunes the empty rule either:
 * `getFormattingRulesCount` counts `labelFormatting.length` as it stands and it saves with the
 * viewpoint, so a stray row would warn about dropped labels that do not exist.
 */
export const hasComponentFieldLabels = (
  labelFormatting: LabelFormattingInfo[]
) =>
  labelFormatting.some(
    ({ type, fields }) => type === 'component' && fields.length > 0
  );

export const getHasMultipleLabels = (
  labelFormatting: LabelFormattingInfo[],
  entity: 'component' | 'reference'
) => {
  let componentFieldsCount = 0;
  let componentRulesCount = 0;

  for (const rule of labelFormatting) {
    if (rule.type === entity) {
      componentRulesCount++;
      componentFieldsCount += rule.fields.length;
    }

    if (componentFieldsCount > 1 || componentRulesCount > 1) {
      return true;
    }
  }

  return false;
};

export const getIsWarningMessage = (
  labelFormatting: LabelFormattingInfo[],
  viewId: ViewIds
) => {
  switch (viewId) {
    case ViewIds.RELATIONSHIPS_3:
      return (
        getHasMultipleLabels(labelFormatting, 'reference') ||
        getHasMultipleLabels(labelFormatting, 'component')
      );

    case ViewIds.TIMELINE:
      return hasReferenceLabelFormattingRules(labelFormatting);

    case ViewIds.BUBBLE: {
      if (!hasReferenceBubbleChartFeature()) {
        return (
          hasReferenceLabelFormattingRules(labelFormatting) ||
          getHasMultipleLabels(labelFormatting, 'component')
        );
      }
      return (
        getHasMultipleLabels(labelFormatting, 'reference') ||
        getHasMultipleLabels(labelFormatting, 'component')
      );
    }

    /* The same two limitations, asserted identical by this view's own copy in
       `getRestrictedMultilabelFormattingWarningMessage` - the bubble chart's sentence without the
       reference bubble chart flag, with the name swapped. Without a case the notice stayed `info` at the moment the user configured
       the thing being dropped, which is the one time it should escalate. */
    case ViewIds.SURFACE_3D:
    /* And the other three over `buildXyzPoints`, which passes the same empty
       `referenceLabelFilters`. This switch defaults to `false`, so unlike the two exhaustively
       checked message functions it does not fail the build when a view is added to the lists
       above - the notice simply never escalates. */
    case ViewIds.CONTOUR:
    case ViewIds.SPIDER_2:
    case ViewIds.POLAR:
    case ViewIds.SCATTER_3D:
    case ViewIds.BUBBLE_3D:
    /* And the 3D network view, on the same two limitations. Adding it to the list above is what
       stops the rule being written; this is what makes the notice escalate while it is being
       written, and the two have to move together. */
    case ViewIds.NETWORK_3D: {
      return (
        hasReferenceLabelFormattingRules(labelFormatting) ||
        getHasMultipleLabels(labelFormatting, 'component')
      );
    }
    /* Any component label rule, not more than one: these views show the component's name and
       drop the rest, so the first rule is already being dropped. Without a case here the message
       rendered as `info` even while it was describing something the user had configured, which
       is the one time the views above escalate. */
    case ViewIds.HIERARCHICAL_TREEMAP:
    case ViewIds.HIERARCHICAL_PACKING:
    case ViewIds.SUNBURST:
      return (
        hasReferenceLabelFormattingRules(labelFormatting) ||
        hasComponentFieldLabels(labelFormatting)
      );

    default:
      return false;
  }
};

type GetRestrictedMultiLabelFormattingWarningArgs = {
  viewId: ViewIds;
  labelFormatting: LabelFormattingInfo[];
};
export const getRestrictedMultiLabelFormattingWarningProps = ({
  viewId,
  labelFormatting,
}: GetRestrictedMultiLabelFormattingWarningArgs):
  | NotificationData
  | undefined => {
  const isLabelFormattingRestricted =
    isViewWithoutFullyMultiLabelsSupport(viewId);

  if (!isLabelFormattingRestricted) {
    return;
  }

  const message = getRestrictedMultilabelFormattingWarningMessage(viewId);

  const isWarning = message
    ? getIsWarningMessage(labelFormatting, viewId)
    : false;

  return message
    ? {
        message,
        type: isWarning ? 'warning' : 'info',
      }
    : undefined;
};
